import { apiClient } from './axios';

/** Fișa de registru a unui pacient (date importate din documente și validate). */

export type RegistryProgram = 'DERMATO' | 'HEPATO';

export interface RegistryDocument {
  id: string;
  kind: string;
  fileName: string;
  fileKey: string;
  documentDate: string | null;
}

export interface RegistryEncounter {
  id: string;
  type: 'HOSPITALIZATION' | 'OBSERVATION' | 'AMBULATORY_LETTER' | 'ONCOLOGY_CONSULT' | string;
  unit: string | null;
  startDate: string | null;
  endDate: string | null;
  principalIcd10: string | null;
  secondaryIcd10: string[];
  procedures: Array<{ code?: string | null; name: string; date?: string | null }> | null;
  diagnosisText: string | null;
  epicrisis: string | null;
  recommendations: string | null;
  sourceDocumentId?: string | null;
}

export interface RegistryCaseDiagnosis {
  text: string | null;
  icd10: string | null;
  icdO3Morphology: string | null;
  basis: string | null;
  category: string | null;
  incidenceDate: string | null;
  tumorType?: string | null;
  /** documentul din care a fost derivat diagnosticul (dermato) */
  derivedFromDocumentId?: string | null;
}

export interface RegistryCase {
  lesionId?: string;
  code: string;
  location?: string | null;
  presumptiveIcd10?: string | null;
  /** documentul-sursă al leziunii (dermato) */
  sourceDocumentId?: string | null;
  diagnosis: RegistryCaseDiagnosis | null;
  histopathology?: {
    bulletinNo: string | null;
    status: string | null;
    collectedDate: string | null;
    resultDate: string | null;
    conclusion: string | null;
    margins: string | null;
    breslowMm: number | string | null;
    topographyIcd10: string | null;
    ihc: string | null;
    sourceDocumentId?: string | null;
  } | null;
  surgeries?: Array<{ date: string | null; procedureType: string | null; notes: string | null; sourceDocumentId?: string | null }>;
}

/** Consimțământul de pe chestionarul importat (bife + stare detectată). */
export interface RegistryConsent {
  personalDataProcessing?: boolean | null;
  registryInclusion?: boolean | null;
  futureContact?: boolean | null;
  status?: string | null;
  [key: string]: unknown;
}

export interface RegistryRecord {
  program: RegistryProgram;
  /** id-ul formularului (HepatoForm/DermatoForm) — folosit la exporturile PDF; null pe versiuni vechi ale API-ului */
  formId: string | null;
  validationStatus: string | null;
  consent: RegistryConsent | null;
  /** secțiunile chestionarului importat (chei diferite pe hepato/dermato) */
  questionnaire?: Record<string, unknown> | null;
  documents: RegistryDocument[];
  encounters: RegistryEncounter[];
  cases: RegistryCase[];
  /** doar hepato */
  histopathologies?: Array<{ bulletinNo: string | null; resultDate: string; tumorType: string; details: Record<string, unknown> | null; sourceDocumentId?: string | null }>;
  labs?: Array<{ collectedAt: string | null; testCode: string | null; testName: string; value: string; unit: string | null; refRange: string | null; flag: string | null; sourceDocumentId?: string | null }>;
  afp?: Array<{ collectedAt: string; value: number; unit: string; result: string; sourceDocumentId?: string | null }>;
  treatments?: Array<{ decisionDate: string; details: Record<string, unknown> | null; sourceDocumentId?: string | null }>;
}

export const registryRecordApi = {
  /** null dacă pacientul nu are fișă de registru în programul cerut */
  get: async (program: RegistryProgram, patientId: string): Promise<RegistryRecord | null> => {
    const url = program === 'DERMATO' ? `/dermato/registry-record/${patientId}` : `/hepato-registry/${patientId}`;
    try {
      const response = await apiClient.get(url);
      return (response.data?.data ?? response.data) as RegistryRecord;
    } catch (error) {
      if ((error as { response?: { status?: number } }).response?.status === 404) return null;
      throw error;
    }
  },

  /** Deschide PDF-ul original al documentului într-un tab nou. */
  openSourceFile: async (fileKey: string): Promise<void> => {
    const response = await apiClient.get('/registry-import/source-file', {
      params: { key: fileKey },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data as Blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};
